"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ClientLogger } from "@/lib/clientLogger";
import { getAssistantBaseUrl, getAccessToken, getDevAdminMode } from "@/lib/authFetch";
import i18n from "@/lib/i18n";

export type AuthScheme = "Basic" | "Bearer";

export type WhoamiUser = {
  username: string;
  scheme: AuthScheme;
  userId: string | null;
  tenantId: string | null;
  permissions: string[];
};

export type AuthStatus = "loading" | "authed" | "anonymous";

type AuthContextValue = {
  user: WhoamiUser | null;
  scheme: AuthScheme | null;
  status: AuthStatus;
  refresh: () => Promise<AuthStatus>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  scheme: null,
  status: "loading",
  refresh: async () => "anonymous",
  logout: async () => {},
});

const FOCUS_DEBOUNCE_MS = 30_000;

async function fetchWhoami(): Promise<WhoamiUser | null> {
  const baseUrl = getAssistantBaseUrl();
  const url = `${baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`}api/auth/whoami`;

  // Try Bearer first if available — covers WebView2 cold-load before any cookie exists.
  // If getAccessToken() throws (standalone browser, no SDK), the cookie still goes
  // along automatically because we use credentials: "same-origin".
  let authHeader: string | undefined;
  try {
    const token = await getAccessToken();
    authHeader = `Bearer ${token}`;
  } catch {
    // No Bearer available → fall through to cookie-only request.
  }

  const headers: Record<string, string> = {};
  if (authHeader) headers.Authorization = authHeader;
  if (getDevAdminMode()) headers["X-Admin-Mode"] = "true";

  const res = await fetch(url, { headers, credentials: "same-origin" });
  if (res.status === 200) {
    const body = (await res.json()) as WhoamiUser & { permissions?: string[] };
    return {
      ...body,
      permissions: body.permissions ?? [],
    };
  }
  if (res.status === 401) return null;

  // 5xx / network: surface as a thrown error so callers can keep status="loading"
  // and let the IDP-banner machinery (Phase 4) take over instead of forcing
  // a redirect to /login. Message is a localized categorical string, not the
  // raw HTTP status — anything caught and routed to ClientLogger stays scrubbed.
  throw new Error(i18n.t("error.code.authentication_failed"));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<WhoamiUser | null>(null);
  const lastProbeAt = useRef<number>(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh intentionally preserves the last observed auth status.
  const refresh = useCallback(async (): Promise<AuthStatus> => {
    try {
      const whoami = await fetchWhoami();
      lastProbeAt.current = Date.now();
      if (whoami) {
        setUser(whoami);
        setStatus("authed");
        return "authed";
      }
      setUser(null);
      setStatus("anonymous");
      return "anonymous";
    } catch (e) {
      ClientLogger.warn("whoami probe failed transiently", {
        error: e instanceof Error ? e : undefined,
        component: "AuthContext",
      });
      // Keep prior status (loading on first probe; authed on subsequent failures).
      // Anonymous is reserved for confirmed 401 — transient errors must not log
      // the user out.
      return status;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(async () => {
    try {
      const baseUrl = getAssistantBaseUrl();
      const url = `${baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`}api/auth/session`;
      await fetch(url, { method: "DELETE", credentials: "same-origin" });
    } catch (e) {
      ClientLogger.warn("Logout request failed; clearing local state anyway", {
        error: e instanceof Error ? e : undefined,
        component: "AuthContext",
      });
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  // Initial probe + focus-driven refresh (debounced).
  useEffect(() => {
    void refresh();
    const onFocus = () => {
      if (Date.now() - lastProbeAt.current < FOCUS_DEBOUNCE_MS) return;
      void refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{
        user,
        scheme: user?.scheme ?? null,
        status,
        refresh,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
